import { useId, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ShiftOutcome, ShiftOutcomeKind } from '../../partner/index.js'
import { OUTCOME_KINDS, formatMonth, groupOutcomesByMonth, type OutcomeGroup } from './outcomeGroups.js'

// Coral marks a negative in this dashboard (see workerRole.ts); a no-show is
// the stronger of the two, so only it is filled.
const KIND_PRESENTATION: Record<ShiftOutcomeKind, { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'bg-green-lt text-green border-green/40' },
  callout: { label: 'Call-out', className: 'bg-coral-lt text-coral border-coral/40' },
  no_show: { label: 'No-show', className: 'bg-coral text-white border-coral' },
}

function formatTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function detail(outcome: ShiftOutcome): string {
  const scheduled = `Scheduled ${formatTime(outcome.scheduledStart)}–${formatTime(outcome.scheduledEnd)}`
  if (outcome.kind === 'completed' && outcome.actualStart && outcome.actualEnd) {
    return `${scheduled} · worked ${formatTime(outcome.actualStart)}–${formatTime(outcome.actualEnd)}`
  }
  if (outcome.kind === 'callout' && outcome.reportedAt) {
    return `${scheduled} · notice given ${formatDateTime(outcome.reportedAt)}`
  }
  return scheduled
}

/** One reported shift as a list row: its kind, date, unit and times. */
export function OutcomeRow({ outcome }: { outcome: ShiftOutcome }) {
  const kind = KIND_PRESENTATION[outcome.kind]
  return (
    <li className="px-3 py-2 flex items-start gap-3">
      <span className={`shrink-0 w-20 text-center text-[10px] font-bold px-1.5 py-0.5 border ${kind.className}`}>{kind.label}</span>
      <div className="min-w-0">
        <div className="text-[11px] font-bold text-ink">
          {outcome.shiftDate} <span className="font-normal text-ink-mid">· unit {outcome.unitId}</span>
          {outcome.clinicalBridge && <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded bg-marine-lt text-marine-dk border-marine/30">Clinical Bridge</span>}
          {outcome.chartCompleted !== undefined && (
            <span className={`ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${outcome.chartCompleted ? 'bg-green-lt text-green border-green/30' : 'bg-gold-lt text-ink border-gold/60'}`}>
              {outcome.chartCompleted ? 'Charted' : 'Chart not completed'}
            </span>
          )}
        </div>
        <div className="text-[10.5px] text-ink-mid">{detail(outcome)}</div>
      </div>
    </li>
  )
}

interface GroupSummaryProps {
  group: OutcomeGroup
  isOpen: boolean
  /** The id of the month's detail list, which exists only while it is open. */
  detailId: string
  onToggle: () => void
}

/** One month's line: the month, how many shifts, and a count per kind in the kind's own colours - a kind with none is left out. */
function GroupSummary({ group, isOpen, detailId, onToggle }: GroupSummaryProps) {
  const Chevron = isOpen ? ChevronDown : ChevronRight
  const total = group.outcomes.length
  return (
    <button
      type="button"
      aria-expanded={isOpen}
      aria-controls={isOpen ? detailId : undefined}
      onClick={onToggle}
      className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-canvas"
    >
      <Chevron aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-marine" />
      <span className="text-[11px] font-bold text-ink">{formatMonth(group.month)}</span>
      <span className="text-[10.5px] text-ink-mid">
        {total} {total === 1 ? 'shift' : 'shifts'}
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        {OUTCOME_KINDS.filter((kind) => group.counts[kind] > 0).map((kind) => (
          <span key={kind} className={`text-[10px] font-bold px-1.5 py-0.5 border ${KIND_PRESENTATION[kind].className}`}>
            {group.counts[kind]} {KIND_PRESENTATION[kind].label}
          </span>
        ))}
        {group.clinicalBridge > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 border rounded bg-marine-lt text-marine-dk border-marine/30">{group.clinicalBridge} Clinical Bridge</span>
        )}
      </span>
    </button>
  )
}

/**
 * A worker's reported shift outcomes - the raw record behind a reliability
 * score - summarized one line per month, most recent first, with a count
 * of each kind; opening a month shows its shifts. The latest month is open
 * unless closed by hand, since it is the one a running score is being
 * built from - derived from the data rather than stored, so a month that
 * arrives later (the first outcome of a new month, or the first ever in
 * the simulator) opens too. Shows what partners reported; it does not
 * score anything.
 */
export function OutcomeLog({ outcomes }: { outcomes: ShiftOutcome[] }) {
  const groups = groupOutcomesByMonth(outcomes)
  /** The months whose open state the reader has flipped from its default. */
  const [toggled, setToggled] = useState<ReadonlySet<string>>(() => new Set())
  const baseId = useId()

  if (groups.length === 0) {
    return <div className="text-[11px] text-ink-mid">No shift outcomes reported yet.</div>
  }

  const latestMonth = groups[0]?.month
  const isOpen = (month: string): boolean => (month === latestMonth) !== toggled.has(month)

  function toggle(month: string): void {
    setToggled((current) => {
      const next = new Set(current)
      if (!next.delete(month)) {
        next.add(month)
      }
      return next
    })
  }

  return (
    <ul className="bg-white border border-marine/15 divide-y divide-marine/10">
      {groups.map((group) => {
        const detailId = `${baseId}-${group.month}`
        return (
          <li key={group.month}>
            <GroupSummary group={group} isOpen={isOpen(group.month)} detailId={detailId} onToggle={() => toggle(group.month)} />
            {isOpen(group.month) && (
              <ul id={detailId} className="border-t border-marine/10 bg-canvas/40 divide-y divide-marine/10">
                {group.outcomes.map((outcome) => (
                  <OutcomeRow key={outcome.eventId} outcome={outcome} />
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </ul>
  )
}
